import { Box, Typography, Skeleton } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';

export interface DashboardBarChartProps {
  displayName: string;
  data: Record<string, unknown>[];
  measure: string;
  groupBy: string;
  loading?: boolean;
}

export function DashboardBarChart({
  displayName,
  data,
  measure,
  groupBy,
  loading,
}: DashboardBarChartProps) {
  const safeData = Array.isArray(data)
    ? data.map((row) => {
        const cleaned: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(row)) {
          cleaned[k] = v ?? 0;
        }
        return cleaned;
      })
    : [];

  if (loading || safeData.length === 0) {
    return (
      <Box sx={{ p: 2, height: '100%' }}>
        <Skeleton width="40%" height={24} />
        <Skeleton variant="rectangular" height="calc(100% - 40px)" sx={{ mt: 1, borderRadius: 1 }} />
      </Box>
    );
  }

  // Format x-axis labels for date-based groupBy fields
  const xAxisValueFormatter = groupBy === 'month' || groupBy === 'date'
    ? (value: unknown) => {
        const d = new Date(String(value));
        if (isNaN(d.getTime())) return String(value);
        if (groupBy === 'month') {
          return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
        }
        return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      }
    : undefined;

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {displayName}
      </Typography>
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <BarChart
          dataset={safeData}
          xAxis={[{ dataKey: groupBy, scaleType: 'band', valueFormatter: xAxisValueFormatter }]}
          series={[{ dataKey: measure, label: measure, valueFormatter: (v) => v == null ? '' : String(v) }]}
          slotProps={{ tooltip: { trigger: 'item' } }}
          height={300}
          margin={{ top: 20, right: 20, bottom: 30, left: 60 }}
        />
      </Box>
    </Box>
  );
}
